/**
 * Cognito access for the capture client. Direct SDK flow through
 * `amazon-cognito-identity-js`, matching the sibling portal
 * (`apps/classification-ui/src/components/auth/cognitoService.jsx`): no
 * hosted UI, no OAuth redirect, no callback URL. Both clients sign in
 * against the same pool the same way (PRIN-SC-4, one identity system).
 *
 * Token storage is the SDK's own localStorage keys, which is what makes a
 * reload survivable on a phone: `restoreSession` reads them back and the
 * SDK refreshes with the stored refresh token when the ID token has aged
 * out. This module never reads the refresh token itself and never logs.
 *
 * Pool id, client id and region come from Vite env vars declared in
 * `src/vite-env.d.ts`. A token minted from any other pool or client is
 * rejected by the API Gateway JWT authorizer, so these are configuration,
 * never constants in source.
 */

import './polyfills';

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

import { readTokenExpiry, userFromClaims, decodeJwtClaims } from './token';
import type { AuthSession } from './types';

export class AuthConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthConfigError';
  }
}

/** Thrown when Cognito answers the sign-in with a challenge this client
 * cannot complete. The operator's password was set by their partner admin,
 * so a forced password change is resolved in the portal, not here. */
export class AuthChallengeError extends Error {
  readonly challenge: string;

  constructor(challenge: string, message: string) {
    super(message);
    this.name = 'AuthChallengeError';
    this.challenge = challenge;
  }
}

let cachedPool: CognitoUserPool | null = null;

export function getUserPool(): CognitoUserPool {
  if (cachedPool) return cachedPool;

  const userPoolId = import.meta.env.VITE_CAPTURE_COGNITO_USER_POOL_ID;
  const clientId = import.meta.env.VITE_CAPTURE_COGNITO_CLIENT_ID;
  const region = import.meta.env.VITE_CAPTURE_COGNITO_REGION;

  const missing = [
    userPoolId ? null : 'VITE_CAPTURE_COGNITO_USER_POOL_ID',
    clientId ? null : 'VITE_CAPTURE_COGNITO_CLIENT_ID',
    region ? null : 'VITE_CAPTURE_COGNITO_REGION',
  ].filter((name): name is string => name !== null);

  if (missing.length > 0) {
    throw new AuthConfigError(`Sign-in is not configured for this build. Missing: ${missing.join(', ')}.`);
  }

  cachedPool = new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: clientId,
    // Same value the SDK derives from the pool id, stated explicitly so a
    // build pointed at another region fails on its own configuration
    // rather than on a silent default.
    endpoint: `https://cognito-idp.${region}.amazonaws.com/`,
  });
  return cachedPool;
}

function sessionFrom(session: CognitoUserSession): AuthSession | null {
  const idToken = session.getIdToken().getJwtToken();
  const claims = decodeJwtClaims(idToken);
  if (!claims) return null;
  return {
    idToken,
    expiresAt: readTokenExpiry(idToken),
    user: userFromClaims(claims),
  };
}

/** Authenticates against the pool and returns the ID token the API
 * Gateway authorizer accepts. Rejects with `AuthChallengeError` when
 * Cognito wants a password change or MFA, and with the SDK's own error
 * otherwise; callers map those to a message with `signInErrorMessage`. */
export function signIn(email: string, password: string): Promise<AuthSession> {
  const pool = getUserPool();
  const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
  const authDetails = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise<AuthSession>((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        const parsed = sessionFrom(session);
        if (!parsed) {
          reject(new Error('Cognito returned a token this client could not read.'));
          return;
        }
        resolve(parsed);
      },
      onFailure: (error) => reject(error),
      newPasswordRequired: () => {
        reject(
          new AuthChallengeError(
            'NEW_PASSWORD_REQUIRED',
            'This account needs a new password. Set it in the partner portal, then sign in here.',
          ),
        );
      },
      mfaRequired: () => {
        reject(new AuthChallengeError('MFA_REQUIRED', 'This account requires a verification code, which this app cannot collect yet.'));
      },
    });
  });
}

/** Reads the stored session back after a reload or a relaunch from the
 * home screen. `getSession` refreshes with the stored refresh token when
 * the ID token has expired, so a signed-in operator returning to the app
 * keeps working. Resolves null when there is nothing usable, which is a
 * normal first launch, not an error. */
export function restoreSession(): Promise<AuthSession | null> {
  let pool: CognitoUserPool;
  try {
    pool = getUserPool();
  } catch {
    return Promise.resolve(null);
  }

  const cognitoUser = pool.getCurrentUser();
  if (!cognitoUser) return Promise.resolve(null);

  return new Promise<AuthSession | null>((resolve) => {
    cognitoUser.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(sessionFrom(session));
    });
  });
}

/** Forces a token refresh from the stored refresh token. Used when a call
 * comes back 401 with a token the client still believed in. Resolves null
 * when the refresh token is itself gone or rejected, which is the
 * unrecoverable case: the caller signs the operator out. */
export function refreshSession(): Promise<AuthSession | null> {
  let pool: CognitoUserPool;
  try {
    pool = getUserPool();
  } catch {
    return Promise.resolve(null);
  }

  const cognitoUser = pool.getCurrentUser();
  if (!cognitoUser) return Promise.resolve(null);

  return new Promise<AuthSession | null>((resolve) => {
    cognitoUser.getSession((sessionError: Error | null, session: CognitoUserSession | null) => {
      if (sessionError || !session) {
        resolve(null);
        return;
      }
      cognitoUser.refreshSession(session.getRefreshToken(), (refreshError, refreshed: CognitoUserSession | null) => {
        if (refreshError || !refreshed) {
          resolve(null);
          return;
        }
        resolve(sessionFrom(refreshed));
      });
    });
  });
}

/** Clears the SDK's stored tokens, including the refresh token. */
export function signOut(): void {
  let pool: CognitoUserPool;
  try {
    pool = getUserPool();
  } catch {
    return;
  }
  pool.getCurrentUser()?.signOut();
}

/** Turns a Cognito failure into something an operator in a warehouse aisle
 * can act on. The submitted password never appears in the result, and the
 * error is not logged anywhere. */
export function signInErrorMessage(error: unknown): string {
  if (error instanceof AuthConfigError || error instanceof AuthChallengeError) {
    return error.message;
  }
  const name = (error as { code?: string; name?: string } | null)?.code ?? (error as { name?: string } | null)?.name;
  switch (name) {
    case 'NotAuthorizedException':
      return 'That email and password combination was not accepted.';
    case 'UserNotFoundException':
      return 'No account exists for that email.';
    case 'UserNotConfirmedException':
      return 'This account is not confirmed yet. Confirm it in the partner portal first.';
    case 'PasswordResetRequiredException':
      return 'This account needs a password reset. Reset it in the partner portal, then sign in here.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Too many sign-in attempts. Wait a moment and try again.';
    case 'NetworkError':
      return 'Cannot reach the sign-in service. Check the connection and try again.';
    default:
      return 'Sign-in failed. Check the connection and try again.';
  }
}
